import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import * as yup from 'yup';

import './EditableInfo.css';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { isReadableOnDark } from '../../utils';
import { BRAND_LEFT, PROFILE_DESC_COUNTER_THRESHOLD, PROFILE_DESC_MAX, PROFILE_TITLE_COUNTER_THRESHOLD, PROFILE_TITLE_MAX } from 'utils/constants';

type EditableInfoProps = {
    currentInfo: { title?: string; description?: string };
    onHandleEditInfo: (type: 'title' | 'description', value: string) => void;
};

type ProfileTheme = {
    background?: { gradient?: { left?: string } };
};

const titleSchema = yup.object({ title: yup.string().max(PROFILE_TITLE_MAX) });
const descSchema = yup.object({ description: yup.string().max(PROFILE_DESC_MAX) });

const EditableInfo = ({ currentInfo, onHandleEditInfo }: EditableInfoProps) => {
    const { t } = useTranslation();
    const { user: { username } } = useAuth() as any;
    const { state, loading } = useProfile();
    const titleRef = useRef<HTMLTextAreaElement>(null);
    const descRef = useRef<HTMLTextAreaElement>(null);

    const titleValue = currentInfo?.title ?? '';
    const descValue = currentInfo?.description ?? '';

    const [errorTitle, setErrorTitle] = useState(titleValue.length > 0 && !titleSchema.isValidSync({ title: titleValue }));
    const [errorDesc, setErrorDesc] = useState(descValue.length > 0 && !descSchema.isValidSync({ description: descValue }));
    const [nameFocused, setNameFocused] = useState(false);

    useLayoutEffect(() => {
        if (titleRef.current) {
            titleRef.current.style.height = 'auto';
            titleRef.current.style.height = `${titleRef.current.scrollHeight}px`;
        }
    }, [titleValue]);

    useLayoutEffect(() => {
        if (descRef.current) {
            descRef.current.style.height = 'auto';
            descRef.current.style.height = `${descRef.current.scrollHeight}px`;
        }
    }, [descValue]);

    useEffect(() => {
        setErrorTitle(titleValue.length > PROFILE_TITLE_MAX);
        setErrorDesc(descValue.length > PROFILE_DESC_MAX);
    }, [titleValue, descValue]);

    const theme = state.currentProfile.theme as ProfileTheme;
    const rawUserColor = theme?.background?.gradient?.left ?? BRAND_LEFT;
    const userColor = isReadableOnDark(rawUserColor) ? rawUserColor : '#ffffff';

    const handleChangeTitle = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onHandleEditInfo('title', e.target.value);
    };

    const handleChangeDesc = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onHandleEditInfo('description', e.target.value);
    };

    const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter') e.preventDefault();
    };

    const titleCount = titleValue.length;
    const descCount = descValue.length;
    const showTitleCounter = titleCount >= PROFILE_TITLE_COUNTER_THRESHOLD;
    const showDescCounter = descCount >= PROFILE_DESC_COUNTER_THRESHOLD;
    const showFallbackHint = !nameFocused && titleValue.length === 0 && !!username;

    const cssVars = { ['--field-user-color' as string]: userColor } as React.CSSProperties;

    if (loading) {
        return (
            <div className="signature-info-skeleton">
                <Skeleton variant="text" width="60%" height={40} />
                <Skeleton variant="text" width="40%" height={18} />
                <Skeleton variant="text" width="90%" height={22} />
                <Skeleton variant="text" width="75%" height={22} />
            </div>
        );
    }

    return (
        <div className="signature-info" style={cssVars}>
            <div className="signature-field signature-field-name">
                <textarea
                    ref={titleRef}
                    className={`signature-input signature-input-name${errorTitle ? ' is-error' : ''}`}
                    value={titleValue}
                    placeholder={t('profile.info.name.placeholder')}
                    onChange={handleChangeTitle}
                    onKeyDown={handleTitleKeyDown}
                    onFocus={() => setNameFocused(true)}
                    onBlur={() => setNameFocused(false)}
                    rows={1}
                    maxLength={PROFILE_TITLE_MAX + 20}
                    aria-label={t('profile.info.name.placeholder')}
                />
                <span className="signature-underline" aria-hidden="true" />
                <div className="signature-meta">
                    {showFallbackHint && (
                        <span className="signature-hint">
                            {t('profile.info.name.fallback-hint', { username })}
                        </span>
                    )}
                    {showTitleCounter && (
                        <span className={`signature-counter${errorTitle ? ' is-error' : ''}`}>
                            {titleCount}/{PROFILE_TITLE_MAX}
                        </span>
                    )}
                </div>
            </div>

            <div className="signature-field signature-field-bio">
                <textarea
                    ref={descRef}
                    className={`signature-input signature-input-bio${errorDesc ? ' is-error' : ''}`}
                    value={descValue}
                    placeholder={t('profile.info.desc.placeholder')}
                    onChange={handleChangeDesc}
                    rows={2}
                    maxLength={PROFILE_DESC_MAX + 40}
                    aria-label={t('profile.info.desc.placeholder')}
                />
                <span className="signature-underline" aria-hidden="true" />
                {showDescCounter && (
                    <div className="signature-meta">
                        <span className={`signature-counter${errorDesc ? ' is-error' : ''}`}>
                            {descCount}/{PROFILE_DESC_MAX}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditableInfo;
